import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import type { To } from 'react-router';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import defaultShield from 'assets/club/shield_none.webp';
import { Button } from 'atoms/buttons/Button';
import { Horizontal, Vertical } from 'atoms/layout/flex';
import { LinkBox, LinkOther, LinkOverlay } from 'atoms/navigation/Box';
import { BodyS, HeadlineL, LabelL, LabelS } from 'atoms/typography';
import { proxyUrl } from 'atoms/ui/ResponsiveImg';
import { Avatar } from 'components/user/Avatar';
import { useSportContext } from 'contexts/sport';
import { useQuery } from 'hooks/graphql/useQuery';
import { useDefaultStadiumBackground } from 'hooks/useDefaultStadiumBackground';
import { range } from 'lib/arrays';
import { glossary, navLabels } from 'lib/glossary';

import { AchievementBadgePlaceholder } from './AchievementBadgePlaceholder';
import type {
  MyClubNavItemQuery,
  MyClubNavItemQueryVariables,
  MyClubNavItem_UserSportProfileInterface,
} from './__generated__/index.graphql';

const Root = styled(Vertical).attrs({ gap: 3, center: true })`
  position: relative;
  border-radius: var(--double-unit);
  padding: var(--double-unit);
  border: 2px solid transparent;
  background:
    linear-gradient(rgba(var(--c-rgb-black), 0.5)) padding-box,
    var(--background-url) center / cover no-repeat padding-box,
    linear-gradient(var(--c-black), var(--c-black)) padding-box,
    linear-gradient(
        180deg,
        var(--c-nd-600) 50%,
        var(--c-yellow-300) 70%,
        var(--c-white) 73%,
        var(--c-yellow-300) 76%,
        var(--c-yellow-300) 86%,
        var(--c-white) 89%,
        var(--c-yellow-300)
      )
      border-box;
`;
const ClubBadge = styled.img`
  width: calc(8 * var(--unit));
  height: calc(8 * var(--unit));
`;
const AchievementBadge = styled.img`
  width: var(--quadruple-unit);
  height: var(--quadruple-unit);
  object-fit: contain;
`;

const userProfileFragment = gql`
  fragment MyClubNavItem_UserSportProfileInterface on UserSportProfileInterface {
    id
    clubName
    background {
      id
      pictureUrl
    }
    achievementBadges {
      id
      name
      pictureUrl
    }
    clubBadge {
      id
      name
      pictureUrl
    }
  }
` as TypedDocumentNode<MyClubNavItem_UserSportProfileInterface>;
const MY_CLUB_NAV_ITEM_QUERY = gql`
  query MyClubNavItemQuery($sport: Sport!) {
    currentUser {
      slug
      nickname
      followersCount
      followingCount
      anyUserSportProfile(sport: $sport) {
        id
        background {
          id
          pictureUrl
        }
        clubName
        achievementBadges {
          id
          pictureUrl
          name
        }
        clubBadge {
          id
          pictureUrl
          name
        }
        ...MyClubNavItem_UserSportProfileInterface
      }
      ...Avatar_publicUserInfoInterface
    }
  }
  ${userProfileFragment}
  ${Avatar.fragments.publicUserInfoInterface}
` as TypedDocumentNode<MyClubNavItemQuery, MyClubNavItemQueryVariables>;

type Props = { to: To; onClick?: () => void };
export const MyClubNavItem = ({ to, onClick }: Props) => {
  const { sport } = useSportContext();
  const defaultStadiumBackground = useDefaultStadiumBackground(sport);
  const { data, loading } = useQuery(MY_CLUB_NAV_ITEM_QUERY, {
    variables: { sport },
  });
  const { currentUser } = data || {};

  const { anyUserSportProfile } = currentUser || {};

  const linkProps = { to, onClick };

  if (loading) {
    return <div style={{ width: 340, height: 310 }} />;
  }

  return (
    <LinkBox>
      <Root
        style={
          {
            '--background-url': `url(${proxyUrl(anyUserSportProfile?.background?.pictureUrl || defaultStadiumBackground || '', { cropWidth: 320 })})`,
          } as CSSProperties
        }
      >
        <Vertical center>
          <ClubBadge
            src={anyUserSportProfile?.clubBadge?.pictureUrl || defaultShield}
            alt={anyUserSportProfile?.clubBadge?.name || ''}
          />

          <Horizontal>
            {currentUser ? (
              <Avatar user={currentUser} size={2} rounded />
            ) : (
              <div style={{ height: 'var(--double-unit)' }} />
            )}
            <LinkOverlay as={Link} {...linkProps}>
              <LabelS>{currentUser?.nickname || ' '}</LabelS>
            </LinkOverlay>
          </Horizontal>
          <HeadlineL>{anyUserSportProfile?.clubName || ' '}</HeadlineL>
          <Horizontal>
            <Horizontal gap={0.5}>
              <BodyS color="var(--c-white)">
                <FormattedNumber
                  value={currentUser?.followersCount || 0}
                  compactDisplay="short"
                  notation="compact"
                  maximumFractionDigits={1}
                />
              </BodyS>
              <LabelS color="var(--c-nd-600)">
                <FormattedMessage {...glossary.followers} />
              </LabelS>
            </Horizontal>
            <Horizontal gap={0.5}>
              <BodyS color="var(--c-white)">
                <FormattedNumber
                  value={currentUser?.followingCount || 0}
                  compactDisplay="short"
                  notation="compact"
                  maximumFractionDigits={1}
                />
              </BodyS>
              <LabelS color="var(--c-nd-600)">
                <FormattedMessage {...glossary.following} />
              </LabelS>
            </Horizontal>
          </Horizontal>
        </Vertical>
        <Horizontal>
          {range(3).map(i => {
            const badge = anyUserSportProfile?.achievementBadges?.[i];
            // eslint-disable-next-line react/no-array-index-key
            return badge?.pictureUrl ? (
              <AchievementBadge
                key={i}
                src={badge?.pictureUrl}
                alt={badge?.name || ''}
              />
            ) : (
              <AchievementBadgePlaceholder key={i} size={4} />
            );
          })}
        </Horizontal>
        <LinkOther as="div">
          <Button color="quaternary" size="medium" {...linkProps}>
            <LabelL bold>
              <FormattedMessage {...navLabels.myClub} />
            </LabelL>
          </Button>
        </LinkOther>
      </Root>
    </LinkBox>
  );
};
